import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../styles";

const SidebarContext = React.createContext<"left" | "right">("left");

export interface SidebarLayoutPanelProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Whether the sidebar is open.
   */
  open: boolean;

  /**
   * Animation duration in milliseconds.
   * @default 300
   */
  animationDuration?: number;

  /**
   * CSS transition timing function.
   * @default "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
   */
  animationEasing?: string;

  /**
   * Content to display inside the sidebar.
   */
  children: React.ReactNode;

  /**
   * Additional CSS class names to apply to the sidebar content.
   */
  className?: string;

  /**
   * Fixed width in pixels. If not provided, width will be determined by content.
   */
  fixedWidth?: number | null;
}

/**
 * Sidebar component for use within SidebarLayout.
 * Automatically detects its position (left/right) from context.
 * Supports both fixed width and auto-sizing based on content.
 * Additional props forwarded to the container element.
 */
export const SidebarLayoutPanel = ({
  open,
  animationDuration = 300,
  animationEasing = "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  children,
  className = "",
  fixedWidth = null,
  ...props
}: SidebarLayoutPanelProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const position = React.useContext(SidebarContext);
  const [width, setWidth] = useState(fixedWidth || 0);

  const translateFrom = position === "left" ? "-100%" : "100%";

  useEffect(() => {
    if (fixedWidth) return;
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });

    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, [fixedWidth]);

  return (
    <div
      style={{
        width: open ? width : 0,
      }}
      className={cn("flex-shrink-0 relative overflow-x-visible z-[1100]")}
      {...props}
    >
      <aside
        ref={contentRef}
        style={{
          width: fixedWidth || undefined,
          transform: open ? "translateX(0)" : `translateX(${translateFrom})`,
          transition: `transform ${animationDuration}ms ${animationEasing}`,
        }}
        className={cn(
          "z-40 absolute top-0 bottom-0 overflow-hidden w-fit",
          position === "right" && "right-0",
          className,
        )}
      >
        {children}
      </aside>
    </div>
  );
};

export interface SidebarLayoutProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Left sidebar panel. Should be a SidebarLayout.Panel component.
   */
  leftPanel?: React.ReactElement<SidebarLayoutPanelProps>;

  /**
   * Right sidebar panel. Should be a SidebarLayout.Panel component.
   */
  rightPanel?: React.ReactElement<SidebarLayoutPanelProps>;

  /**
   * Main content to display between the sidebars.
   */
  children: React.ReactNode;

  /**
   * Additional CSS class names to apply to the root element.
   */
  className?: string;
}

/**
 * Layout component that provides a flexible sidebar structure.
 * Supports left and right sidebars with automatic main content sizing.
 * Additional props forwarded to the root element.
 *
 * Sidebars animate smoothly without causing layout reflow during transitions:
 * - Opening: Main content width jumps immediately, then sidebar slides in
 * - Closing: Main content expands, then sidebar slides out while overlaying main content
 */
export const SidebarLayout = ({
  leftPanel,
  rightPanel,
  children,
  className = "",
  ...props
}: SidebarLayoutProps) => {
  return (
    <div className={cn("flex h-full", className)} {...props}>
      {leftPanel && (
        <SidebarContext.Provider value="left">
          {leftPanel}
        </SidebarContext.Provider>
      )}
      <div className="flex-1 min-w-0 h-full">{children}</div>
      {rightPanel && (
        <SidebarContext.Provider value="right">
          {rightPanel}
        </SidebarContext.Provider>
      )}
    </div>
  );
};

SidebarLayout.Panel = SidebarLayoutPanel;
